const VALUES = [
  ["Independent RN Practice", "No affiliation implied with any government agency."],
  ["Federal-Grade Discipline", "Design targets aligned to HIPAA, NIST 800-171, and CMMC."],
  ["Independent RN Judgment", "Every deliverable reflects the RN's own clinical judgment and sign-off."],
  ["Precision Deliverables", "Written scopes, source citations, defensible conclusions."],
];

export function IntroSection() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5">
        <img
          src="https://images.unsplash.com/photo-1758691461990-03b49d969495?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Legal nurse consultant reviewing chart" className="w-full border border-slate-900 h-[520px] object-cover"
        />
      </div>
      <div className="lg:col-span-7">
        <div className="text-caption text-[#B91C1C] mb-4">ABOUT</div>
        <h1 className="text-4xl lg:text-5xl font-serif font-bold text-[#0A192F] mb-6 leading-tight">Independent RN legal nurse consulting — grounded in the record, answerable to the case.</h1>
        <p className="text-slate-700 leading-relaxed text-lg mb-4">
          Integrity First Nurse Advocates LLC is a Registered Nurse-led legal nurse consulting practice serving VA-accredited agents, VA-accredited attorneys, regular attorneys, and federal/DOD-facing clients. Our work is precise, defensible, and delivered under confidentiality protocols engineered for the sensitivity of Veterans medical and military information.
        </p>
        <p className="text-slate-600 leading-relaxed mb-8">
          We combine rigorous chart review with disciplined workflows: conflict clearance, encrypted document exchange, source-cited chronologies, and mandatory human RN sign-off on every deliverable.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {VALUES.map(([t, d], i) => (
            <div key={t} className="border-l-2 border-[#B91C1C] pl-4 py-2" data-testid={`about-value-${i}`}>
              <div className="font-serif font-bold text-[#0A192F]">{t}</div>
              <div className="text-slate-600 text-sm mt-1 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FounderSection() {
  return (
    <section className="border-y border-slate-200 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-12 items-center" data-testid="founder-section">
        <div className="lg:col-span-4">
          <div className="relative border border-slate-900">
            <img
              src="/rn-legal-nurse-consultant.png"
              alt="Founder and RN Legal Nurse Consultant of Integrity First Nurse Advocates LLC"
              className="w-full aspect-square object-cover"
              data-testid="founder-photo"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-[#0A192F] px-4 py-3">
              <div className="text-caption text-white/60">FOUNDER & PRINCIPAL</div>
              <div className="font-serif text-white font-bold">RN Legal Nurse Consultant</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-8">
          <div className="text-caption text-[#B91C1C] mb-4">MEET YOUR CONSULTANT</div>
          <h2 className="text-3xl lg:text-4xl font-serif font-bold text-[#0A192F] mb-6 leading-tight">A Registered Nurse behind every deliverable.</h2>
          <p className="text-slate-700 leading-relaxed text-lg mb-4" data-testid="founder-bio">
            Before becoming an RN Legal Nurse Consultant, our founder served Veterans at the bedside in medical-surgical care at the Department of Veterans Affairs — guiding patients through complex surgeries, recoveries, and the realities of service-connected conditions. That frontline VA experience is the foundation of this practice: an insider's command of VA clinical workflows, documentation standards, and what Veterans actually go through.
          </p>
          <p className="text-slate-700 leading-relaxed text-lg mb-4">
            Because of that experience, the mission to serve Veterans here isn't a market segment — it's personal, and it is unmatched. Every chronology, merit review, and draft work product is inspected and validated by the RN who knows your case before delivery — not a handoff chain.
          </p>
          <p className="text-slate-600 leading-relaxed mb-6">
            When you engage this practice, you work directly with the Registered Nurse who reviewed your records — from conflict clearance through the final, signed work product.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="badge-federal">Licensed RN</span>
            <span className="badge-crimson">Mandatory RN Sign-Off</span>
            <span className="badge-gold">Direct Access</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ScopeNote() {
  return (
    <section className="bg-[#F1F5F9] border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <h2 className="text-2xl lg:text-3xl font-serif font-bold text-[#0A192F] mb-4">A note on scope & candor</h2>
        <p className="text-slate-600 leading-relaxed max-w-3xl">
          We do not imply government endorsement, and we do not claim certifications or accreditations we have not established. Security, compliance, and control mappings referenced on this site are design targets requiring validation against contracts, data types, hosting authorization, and legal review. Nothing on this site is legal advice.
        </p>
      </div>
    </section>
  );
}
